import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  type BookingResponse,
  type InvoiceKind,
  type InvoiceResponse,
} from '@/shared/api';
import { invoiceErrorText } from './invoiceErrors';

/**
 * Alle Rechnungen einer Buchung — Beherbergung und Parkplatz.
 *
 * <p>Die Parkplatzrechnung entsteht normalerweise beim Abschluss des Onboardings über die
 * Outbox. Bleibt sie aus, gab es bisher keinen Weg, sie nachzuholen: sie war an der Buchung
 * nicht einmal sichtbar. Deshalb hier beides — die vollständige Liste und die Handbremse.</p>
 */
export function useBookingInvoices(booking: BookingResponse) {
  const api = useApi();
  const qc = useQueryClient();
  const queryKey = ['invoice', 'booking', booking.id];
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['invoice'] });
  const showError = async (err: unknown) => toast.error(invoiceErrorText(await toApiError(err)));

  const parkingAddons = (booking.addons ?? []).filter((addon) => addon.kind === 'Parking');

  const query = useQuery<InvoiceResponse[]>({
    queryKey,
    queryFn: () => api.invoices.listForBooking(booking.id),
    // Beide Rechnungen entstehen über die Outbox, also erst einen Worker-Durchlauf später. Ein
    // Invalidate direkt nach dem Klick fragt deshalb zu früh und findet nichts — die Rechnung
    // erschien bisher erst beim nächsten Seitenaufbau. Solange ein gebuchter Parkplatz ohne
    // Rechnung dasteht, wird nachgefragt; sobald sie da ist, hört das von selbst auf.
    refetchInterval: (q) =>
      awaitingParkingInvoice(parkingAddons.length, q.state.data) ? POLL_MS : false,
  });

  const createAccommodation = useMutation({
    mutationFn: () => api.invoices.createForBooking(booking.id),
    onSuccess: () => {
      toast.success('Rechnung erstellt');
      invalidate();
    },
    onError: showError,
  });

  const createParking = useMutation({
    mutationFn: () => api.invoices.createParkingForBooking(booking.id),
    onSuccess: (created) => {
      // Der Aufruf ist idempotent und liefert bei fehlender Voraussetzung eine leere Liste.
      // „Erstellt" zu melden, wo nichts entstand, wäre die Art Lüge, die man erst beim
      // Nachsehen bemerkt.
      toast[created.length ? 'success' : 'info'](
        created.length
          ? 'Parkplatzrechnung erstellt'
          : 'Keine Parkplatzrechnung fällig — Onboarding abgeschlossen und Parkplatz gebucht?',
      );
      invalidate();
    },
    onError: showError,
  });

  // Der Builder. Anlegen und sofort hin — der Entwurf ist ohne Positionen noch nichts wert,
  // der Aussteller soll ihn direkt vor sich haben.
  const createDraft = useMutation({
    mutationFn: (kind: Extract<InvoiceKind, 'Manual' | 'DamageClaim'>) =>
      api.invoices.createDraft(booking.id, { kind }),
    onError: showError,
  });

  const discardDraft = useMutation({
    mutationFn: (draftId: string) => api.invoices.discardDraft(draftId),
    onSuccess: () => {
      toast.success('Entwurf verworfen');
      invalidate();
    },
    onError: showError,
  });

  // Kein Invalidate: der Versandmarker steht nicht in der Antwort, die Liste aendert sich nicht.
  // Und „wird versendet" statt „verschickt": die Mail geht ueber die Outbox, hinaus ist sie einen
  // Worker-Durchlauf spaeter. Zustellung zu melden, wo erst die Absicht feststeht, ist die Art
  // Halbwahrheit, die man erst bemerkt, wenn der Kunde erneut nachfragt.
  const resendMail = useMutation({
    mutationFn: () => api.invoices.resendMail(booking.id),
    onSuccess: (count) =>
      toast.success(
        count === 1
          ? 'Rechnung wird per E-Mail versendet'
          : `${count} Belege werden per E-Mail versendet`,
      ),
    onError: showError,
  });

  const cancel = useMutation({
    mutationFn: ({ invoiceId, reason }: { invoiceId: string; reason: string | null }) =>
      api.invoices.cancel(invoiceId, reason),
    onSuccess: () => {
      toast.success('Stornorechnung erzeugt');
      invalidate();
    },
    onError: showError,
  });

  const invoices = query.data ?? [];

  return {
    invoices,
    isLoading: query.isLoading,
    /** Ohne Antwort ist „noch keine Rechnung“ eine Behauptung – die Liste zeigt dann den Fehler. */
    loadError: query.isError && !query.data ? query.error : null,
    /** Nachladen schlug fehl, die Liste steht noch – sie bleibt, mit Hinweis. */
    stale: query.isError && !!query.data,
    reload: () => void query.refetch(),
    reloading: query.isFetching,
    createAccommodation,
    createParking,
    createDraft,
    discardDraft,
    resendMail,
    cancel,
    hasAccommodationInvoice: invoices.some((invoice) => invoice.kind === 'Accommodation'),
    /** Ohne festgeschriebenen Beleg gibt es nichts zu verschicken - dann waere der Knopf eine Falle. */
    hasIssuedInvoice: invoices.some((invoice) => !!invoice.invoiceNumber && !invoice.isCancelled),
    /** Ein gebuchter Parkplatz ohne eigene Rechnung — genau der Fall für die Handbremse. */
    parkingInvoiceMissing: awaitingParkingInvoice(parkingAddons.length, invoices),
  };
}

/** Zwei Sekunden Outbox-Takt plus Luft — schnell genug, dass es wie sofort wirkt. */
const POLL_MS = 3000;

/**
 * Ein gebuchter Parkplatz ohne eigene Rechnung. Das ist beides: der Grund nachzufragen, solange
 * die Outbox noch arbeitet, und der Fall fuer die Handbremse, wenn sie es nie tut.
 */
function awaitingParkingInvoice(
  parkingAddonCount: number,
  invoices: InvoiceResponse[] | undefined,
): boolean {
  const existing = (invoices ?? []).filter((invoice) => invoice.kind === 'Parking').length;
  return parkingAddonCount > existing;
}
